import { supabase } from './supabaseClient';

// Global request cache to prevent duplicate requests
const requestCache = new Map<string, {
  data: any;
  timestamp: number;
  promise?: Promise<any>;
}>();

// Cache expiration time (2 minutes)
const CACHE_EXPIRY = 2 * 60 * 1000;

// Exponential backoff for 522 errors and other connection issues
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      const isLastAttempt = attempt === maxRetries - 1;
      const isRetryableError = error?.status === 522 || 
                              error?.code === 522 || 
                              error?.message?.includes('timeout') ||
                              error?.message?.includes('connection');
      
      if (isLastAttempt || !isRetryableError) {
        throw error;
      }
      
      // Exponential backoff with jitter
      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
      console.warn(`[supabaseUtils] Retryable error on attempt ${attempt + 1}, retrying in ${Math.round(delay)}ms`, error);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw new Error('Max retries exceeded');
};

// Deduplicated request function
export const deduplicatedRequest = async <T>(
  key: string,
  requestFn: () => Promise<T>,
  cacheExpiry: number = CACHE_EXPIRY
): Promise<T> => {
  const cached = requestCache.get(key);
  
  // If we have a valid cached result, return it
  if (cached && Date.now() - cached.timestamp < cacheExpiry) {
    return cached.data as T;
  }
  
  // If there's an ongoing request, wait for it
  if (cached?.promise) {
    return await cached.promise;
  }
  
  // Create new request
  const promise = requestFn();
  requestCache.set(key, { 
    data: null, 
    timestamp: Date.now(),
    promise 
  });
  
  try {
    const result = await promise;
    requestCache.set(key, { 
      data: result, 
      timestamp: Date.now(),
      promise: undefined 
    });
    return result;
  } catch (error) {
    // Remove failed request from cache
    requestCache.delete(key);
    throw error;
  }
};

// Enhanced Supabase query function with retry and deduplication
export const safeSupabaseQuery = async <T>(
  queryFn: () => Promise<{ data: T | null; error: any }>,
  cacheKey?: string,
  options: {
    maxRetries?: number;
    baseDelay?: number;
    cacheExpiry?: number;
    enableCache?: boolean;
  } = {}
): Promise<{ data: T | null; error: any }> => {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    cacheExpiry = CACHE_EXPIRY,
    enableCache = true
  } = options;

  const executeQuery = async () => {
    const result = await queryFn();
    
    // If there's an error, throw it so retry logic can handle it
    if (result.error) {
      throw result.error;
    }
    
    return result;
  };

  try {
    if (enableCache && cacheKey) {
      return await deduplicatedRequest(cacheKey, executeQuery, cacheExpiry);
    } else {
      return await retryWithBackoff(executeQuery, maxRetries, baseDelay);
    }
  } catch (error) {
    return { data: null, error };
  }
};

// Utility function to clear cache
export const clearRequestCache = (key?: string) => {
  if (key) {
    requestCache.delete(key);
  } else {
    requestCache.clear();
  }
};

// Utility function to get cache stats
export const getCacheStats = () => {
  const now = Date.now();
  const entries = Array.from(requestCache.entries());
  const validEntries = entries.filter(([_, value]) => now - value.timestamp < CACHE_EXPIRY);
  
  return {
    totalEntries: entries.length,
    validEntries: validEntries.length,
    expiredEntries: entries.length - validEntries.length
  };
};

// Pre-configured query functions for common patterns
export const safeUserQuery = async (userId: string, select: string = '*') => {
  const cacheKey = `user-${userId}-${select}`;
  return safeSupabaseQuery(
    async () => {
      const result = await supabase.from('users').select(select).eq('id', userId).single();
      return result;
    },
    cacheKey,
    { cacheExpiry: 5 * 60 * 1000 } // 5 minutes for user data
  );
};

export const safeRoleQuery = async (roleId: string | number) => {
  const cacheKey = `role-${roleId}`;
  return safeSupabaseQuery(
    async () => {
      const result = await supabase.from('roles').select('name').eq('id', roleId).single();
      return result;
    },
    cacheKey,
    { cacheExpiry: 10 * 60 * 1000 } // 10 minutes for role data
  );
}; 